'use client';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  IconButton, Tab
} from '@mui/material';
import SnackBar from 'src/components/era-package/era/SnackBar';
import RenderFormSection from 'src/components/era-package/forms/RenderFormSection';
import Button from 'src/components/era-package/forms/Button';
import Buttons from 'src/components/era-package/era/Buttons';
import { useUnsavedAlert } from 'src/utils/era-package/setupUnsavedAlert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClose,
  faFloppyDisk as saveIcon,
  faList, faPlus,
  faSpinnerThird
} from '@fortawesome/pro-regular-svg-icons';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { addTabKeyToOptions } from 'src/utils/era-package/tab-utils';
import AbiEntrySummaryApiUrls from "../../../apiUrls/AbiEntrySummaryApiUrls";
import DataTableSection from "../../../components/era-package/forms/DataTableSection";
import {pga} from "../fields/entry-summary-line";
import {htsRateTaxFormFields} from "../fields/hts-code";

const EditHtsCodeModal = ({
  title = 'Record',
  open,
  onClose,
  onSave,
  onDelete,
  handleRefreshData,
  fields, formFields, name,
  optionsMap,
  initialData,
  isEditing, index
}) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [hasUnsaved, setHasUnsaved] = useState(false);
  const rhfArray = useFieldArray({ name }) || null
  const rhf = useFormContext() || null
  const [resetData, setResetData] = useState(null) // need to cache this data; formData & initialData will change

  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [showFormErrorSnackbar, setShowFormErrorSnackbar] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [loading, setLoading] = useState(false);

  const [value, setValue] = React.useState('basic');

  const fieldIndex = isEditing ? index : rhfArray?.fields?.length
  const indexedName = `${name}.${fieldIndex}`

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  // Refs for focus management
  const dialogRef = useRef(null);

  // Set up unsaved alert functionality
  useUnsavedAlert(hasUnsaved);

  const modalFields = useMemo(() => {

    return !!rhfArray && Array.isArray(formFields)
      ? formFields.map(field => {
        let copy = { ...field }
        copy.name = `${indexedName}.${copy.name}`
        return copy
      })
      : fields
  }, [formFields, indexedName, rhfArray])

  const indexedOptions = useMemo(() => {
    return !!rhfArray
      ? addTabKeyToOptions(optionsMap, indexedName)
      : optionsMap
  }, [optionsMap, indexedName, rhfArray])

  // Comprehensive focus management to prevent aria-hidden conflicts
  useEffect(() => {
    if (open) {
      // Immediately blur all focused elements in main wrapper when modal opens
      const mainWrapper = document.querySelector('.mainwrapper');
      if (mainWrapper) {
        const allFocusedElements = mainWrapper.querySelectorAll(':focus');
        allFocusedElements.forEach(element => {
          element.blur();
        });

        // Also blur any buttons that might retain focus
        const allButtons = mainWrapper.querySelectorAll('button, [role="button"]');
        allButtons.forEach(button => {
          if (document.activeElement === button) {
            button.blur();
          }
        });
      }

      // Set up continuous monitoring to catch any focus that slips through
      const focusMonitor = setInterval(() => {
        const mainWrapper = document.querySelector('.mainwrapper');
        const focusedElement = document.activeElement;

        if (mainWrapper &&
          mainWrapper.getAttribute('aria-hidden') === 'true' &&
          focusedElement &&
          mainWrapper.contains(focusedElement)) {

          // Immediately blur the problematic element
          focusedElement.blur();

          // Force focus to modal
          const dialog = dialogRef.current;
          if (dialog) {
            const firstFocusable = dialog.querySelector(
              'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            if (firstFocusable) {
              firstFocusable.focus();
            }
          }
        }
      }, 50); // More frequent monitoring

      return () => {
        clearInterval(focusMonitor);
      };
    }
  }, [open]);

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
      setResetData({ ...initialData })
    } else {
      // Initialize with default values
      const defaults = {};
      fields.forEach(field => {
        defaults[field.name] = field.defaultValue;
      });
      setFormData(defaults);
    }
    // Reset unsaved state when modal opens/closes or initial data changes
    setHasUnsaved(false);
  }, [initialData, fields, open]);

  const validateForm = async () => {
    const newErrors = {};

    if (!!rhf) {
      if (rhf.getValues(indexedName) !== undefined) {
        const err = await rhf.trigger(indexedName)
        return err
      } else {
        // item is empty.
        setShowErrorSnackbar(true)
        return false
      }
    }

    // no RHF. Manual validation
    modalFields.forEach(field => {
      if (field.required) {
        const value = formData[field.name];
        if (!value || String(value).trim() === '') {
          newErrors[field.name] = `${field.label} is required`;
        }
      }
    });

    setErrors(newErrors);
    console.log('errors', newErrors)
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    console.log("handle save here..")
    //let isValid = await validateForm()
      setLoading(true);
      setHasUnsaved(false); // Clear unsaved flag on successful save

      try {
        const response = await fetch(AbiEntrySummaryApiUrls.EDIT_HTS_CODE, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
          },
          body: JSON.stringify(formData)
        });

        if (!response.ok) {
          console.log('Error saving file');
          setShowFormErrorSnackbar(true);
          setLoading(false);
          return;
        }

        const result = await response.json();
        console.log('File saved successfully:', result);
        onClose();
        handleRefreshData();
        setShowSuccessSnackbar(true);
        setLoading(false);
      } catch (error) {
        console.log('Error saving file');
        setShowFormErrorSnackbar(true);
        setLoading(false);
      }


  };

  const handleClose = () => {
    if (hasUnsaved) {
      const message = 'You have unsaved changes. Are you sure you want to close without saving?';
      if (window.confirm(message)) {
        setHasUnsaved(false);
        if (!!rhfArray) {
          if (isEditing) // reset data for this index
            rhfArray.update(fieldIndex, resetData)
          else
            rhfArray.remove(fieldIndex)
        }
        // set tab back to basic
        onClose();
      }
    } else {
      // set tab back to basic
      onClose();
    }
  };

  // Handle keyboard navigation for accessibility
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      handleClose();
    } else if (event.key === 'Tab') {
      // Ensure focus stays within the modal
      const dialog = dialogRef.current;
      if (dialog) {
        const focusableElements = dialog.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          }
        } else {
          // Tab
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }
    }
  };

  const formSections = [
    {
      title: 'Basic',
      fields: modalFields.filter(field => field.section === "hts")
    },
    {
      title: 'Special Rates / Taxex / Fees',
      icon: <FontAwesomeIcon icon={faList} />,
      type: 'table', name: `${name}.${fieldIndex}.htsRateTaxList`,
      tableComponent: DataTableSection,
      tableFields: htsRateTaxFormFields,
      maxRows: 999,
    },
  ]


  return (<>
    {/* Error Snackbar */}
    <SnackBar
      title="Missing Data"
      type="error"
      open={showErrorSnackbar}
      onClose={() => setShowErrorSnackbar(false)}
      duration={10000}
    >
      Nothing to add.
    </SnackBar>

    <SnackBar
      title="Error"
      type="error"
      open={showFormErrorSnackbar}
      onClose={() => setShowFormErrorSnackbar(false)}
      duration={10000}
    >
      Internal error occured, please try again later.
    </SnackBar>

    <SnackBar
      title="Saved successfully"
      type="success"
      open={showSuccessSnackbar}
      onClose={() => setShowSuccessSnackbar(false)}
      duration={10000}
    >
      Line Item saved successfully
    </SnackBar>

    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="100dvw"
      fullWidth
      ref={dialogRef}
      PaperProps={{
        sx: {
          maxHeight: '90vh',
        },
      }}
      TransitionProps={{
        onEntered: () => {
          // Comprehensive cleanup of any remaining focused elements in main wrapper
          const mainWrapper = document.querySelector('.mainwrapper');
          if (mainWrapper) {
            // Find and blur any focused elements
            const focusedElements = mainWrapper.querySelectorAll(':focus');
            focusedElements.forEach(element => {
              element.blur();
            });

            // Extra safety: blur any MUI buttons that might be retaining focus
            const muiButtons = mainWrapper.querySelectorAll('.MuiButton-root, .MuiButtonBase-root');
            muiButtons.forEach(button => {
              if (document.activeElement === button) {
                button.blur();
              }
            });
          }

          // Ensure focus is properly set in the modal
          setTimeout(() => {
            const dialog = dialogRef.current;
            if (dialog) {
              const firstFocusable = dialog.querySelector(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
              );
              if (firstFocusable) {
                firstFocusable.focus();
              }
            }
          }, 10);
        }
      }}
      // Ensure proper focus containment
      disableEnforceFocus={false}
      disableAutoFocus={false}
      disableRestoreFocus={false}
      onKeyDown={handleKeyDown}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      role="dialog"
    >
      <DialogTitle
        id="modal-title"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        {`${isEditing ? 'Edit' : 'Add'} ${title}`}
        <IconButton
          aria-label={`Close ${isEditing ? 'edit' : 'add'} ${title} modal`}
          onClick={handleClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
          title="Close modal"
        >
          <Box component={FontAwesomeIcon} icon={faClose} sx={{ width: '1em' }} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers id="modal-description">
        <Box sx={{ py: 2 }}>
          {formSections.map((section) => (
              <RenderFormSection
                section={section}
                optionsMap={indexedOptions}
                formData={formData}
                setFormData={setFormData}
                errors={errors}
                arrayKey={indexedName}
                setErrors={setErrors}
                setHasUnsaved={setHasUnsaved}
                globalAccordionAction={null} // Not needed for modal
              />
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ pt: 3 }}>
        <Buttons
          advance={
            <Button
              onClick={handleSave}
              variant="contained"
              title={isEditing ? 'Save changes' : 'Add this record'}
              disabled={loading}
              startIcon={<FontAwesomeIcon icon={loading ? faSpinnerThird : (isEditing ? saveIcon : faPlus)} width={16} spin={loading} />}
            >
              {isEditing ? 'Save' : 'Add'}
            </Button>
          }
          other={
            <>
              <Button variant="outlined" onClick={handleClose} title="Go back">
                Cancel
              </Button>
              {isEditing && (
                <Button
                  variant="outlined"
                  onClick={onDelete}
                  color="error"
                  title="Proceed with Caution: Delete record"
                >
                  Delete
                </Button>
              )}
            </>
          }
        />
      </DialogActions>
    </Dialog>
  </>);
};

export default EditHtsCodeModal;
